import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import {
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import useSelfDashboard from "PlatForms/SelfLMS/Hooks/SelfLearner/useSelfDashboard";
import { ProfileContext } from "Common/hooks/context/ProfileContext";
import { SelfHeader } from "./SelfHeader";
import SimplifiedInterestAlert from "./InterestAlert";
import RecommendedCoursesCard from "./RecomendedCourseCard";
import EnrolledCoursesCard from "./EnrolledCourseCard";

const SelfLandingPage = () => {
  const navigate = useNavigate();
  const { isProfileComplete, loading: profileLoading } = useContext(ProfileContext);
  const { dashboardData, interest, loading, error } = useSelfDashboard();

  if (loading || profileLoading) {
    return (
      <>
        <div className="sl-skel sl-skel--hero" />
        <div className="sl-stats" style={{ border: "none", background: "transparent", gap: 16, marginBottom: 24 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="sl-skel sl-skel--stat" key={i} />
          ))}
        </div>
        <div className="sl-grid">
          {[0, 1, 2].map((i) => (
            <div className="sl-skel sl-skel--card" key={i} />
          ))}
        </div>
      </>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="sl-empty">
        <div className="sl-empty__icon">
          <ExclamationCircleOutlined />
        </div>
        <h3 className="sl-empty__title">{error ? "Something went wrong" : "No data available"}</h3>
        <p className="sl-empty__text">
          {error ? "We could not load your dashboard. Please refresh and try again." : "Check back shortly."}
        </p>
        <Button type="primary" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </div>
    );
  }

  const learner = dashboardData.learner || {};
  const userName = `${learner.firstName || ""} ${learner.lastName || ""}`.trim();
  const gate = {
    opacity: isProfileComplete ? 1 : 0.5,
    pointerEvents: isProfileComplete ? "auto" : "none",
  };
  const progressPct = dashboardData.progress ? Math.min(100, parseFloat(dashboardData.progress)) : 0;

  const stats = [
    {
      label: "Enrolled",
      value: dashboardData.enrollmentCount || 0,
      icon: <BookOutlined />,
      color: "#9A6B0E",
      soft: "rgba(255, 106, 20, 0.13)",
    },
    {
      label: "Completed",
      value: dashboardData.enrollmentCompletedCount || 0,
      icon: <CheckCircleOutlined />,
      color: "#34d399",
      soft: "rgba(52, 211, 153, 0.14)",
    },
    {
      label: "In progress",
      value: dashboardData.enrollmentInprogressCount || 0,
      icon: <ClockCircleOutlined />,
      color: "#fbbf24",
      soft: "rgba(251, 191, 36, 0.14)",
    },
    {
      label: "Overall progress",
      value: progressPct.toFixed(0),
      suffix: "%",
      bar: progressPct,
      icon: <TrophyOutlined />,
      color: "#9A6B0E",
      soft: "rgba(255, 106, 20, 0.13)",
    },
    {
      label: "Certificates",
      value: dashboardData.totalCertificates || 0,
      icon: <SafetyCertificateOutlined />,
      color: "#9A6B0E",
      soft: "rgba(96, 165, 250, 0.14)",
    },
  ];

  return (
    <>
      <SelfHeader userName={userName} learnerCode={learner.learnerCode} />

      {!isProfileComplete && (
        <div className="sl-alert sl-alert--warn">
          <div className="sl-alert__main">
            <span className="sl-alert__icon">
              <ExclamationCircleOutlined />
            </span>
            <div>
              <div className="sl-alert__title">Complete your profile to unlock learning</div>
              <div className="sl-alert__text">
                Enrollment and other actions stay locked until your profile is complete.
              </div>
            </div>
          </div>
          <Button type="primary" onClick={() => navigate("/selfLearner-profile")}>
            Complete profile
          </Button>
        </div>
      )}

      <SimplifiedInterestAlert interests={interest || []} />

      {/* Learning summary */}
      <div className="sl-stats" style={gate}>
        {stats.map((s) => (
          <div className="sl-stat" key={s.label}>
            <div className="sl-stat__top">
              <span className="sl-stat__icon" style={{ background: s.soft, color: s.color }}>
                {s.icon}
              </span>
              <span className="sl-stat__label">{s.label}</span>
            </div>
            <div className="sl-stat__val">
              {s.value}
              {s.suffix && <small>{s.suffix}</small>}
            </div>
            {s.bar !== undefined && (
              <div className="sl-stat__bar">
                <span style={{ transform: `scaleX(${s.bar / 100})` }} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Recommended + enrolled */}
      <div style={gate}>
        {dashboardData.recommendedCourses && dashboardData.recommendedCourses.length > 0 && (
          <RecommendedCoursesCard recommendedCourses={dashboardData.recommendedCourses} />
        )}
        <EnrolledCoursesCard />
      </div>
    </>
  );
};

export default SelfLandingPage;
